<template>
	<div>
		<alert-basic
			:show="alert.show"
			:message="alert.message"
			:type="alert.type"
		/>
		<!-- Filters -->
		<validity-checker-filter
			:appNameShort="appNameShort"
			:isLoading="isLoading"
			:sort="sort"
			:search="search"
			@submit="submit"
			@excelExport="excelExport"
		/>

		<!-- Progress bar -->
		<progress-bar
			:isLoading="isLoading"
		/>

		<!-- pagination -->
		<validity-checker-pagination
			:itemsCount="itemsCount"
			:key="componentKey"
			@paginationCurrentPageChanged="submitNewPage"
		/>

		<!-- Data table -->
		<validity-checker-table
			:itemsVc="itemsVc"
			:doneFirstFetching="doneFirstFetching"
			:lastVcDataUpdateForMandator="lastVcDataUpdateForMandator"
			:search.sync="search"
			:key="componentKey+1"
			:hideSpCols="applyServiceProviderRestriction"
			:hide-generic-usage-key-col="applyGenericUsageRestriction"
			v-show="clickedSubmit"
			@sortHasChanged="submitSort"
		/>

		<v-divider class="dividerVc"></v-divider>
	</div>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import ValidityCheckerFilter from './ValidityCheckerFilter.vue'
import ValidityCheckerTable from './ValidityCheckerTable.vue'
import ProgressBar from '../vueBasics/Progressbar.vue'
import ValidityCheckerPagination from './ValidityCheckerPagination.vue'
import AlertBasic from '../vueBasics/AlertBasic.vue'

export default {
	name: 'ValidityCheckerReport',
	components: {
		ValidityCheckerFilter,
		ValidityCheckerTable,
		ProgressBar,
		ValidityCheckerPagination,
		AlertBasic,
	},
	data() {
		return {
			appNameShort: 'vc',
			clickedSubmit: false,
			itemsVc: [],
			isLoading: false,
			itemsCount: 0,
			doneFirstFetching: false,
			lastVcDataUpdateForMandator: '',
			filters: Object,
			pagination: Number,
			sort: Object,
			search: String,
			componentKey: 0,
			applyServiceProviderRestriction: false,
			applyGenericUsageRestriction: false,
			filterId: null,
			previousSort: {
				page: 1,
				itemsPerPage: 100,
				sortBy: ['usage_date'],
				sortDesc: [true],
				groupBy: [],
				groupDesc: [],
				mustSort: false,
				multiSort: false,
			},
			alert: {
				show: false,
				message: '',
				type: 'info',
			},
		}
	},
	watch: {
		search: {
			handler(value) {
				if (value && value.length > 0) this.submitSearch()
			},
		},
	},
	methods: {
		forceRender() {
			this.componentKey += 1
		},
		async submit(values) {
			try {
				this.filters = values.formState
				this.pagination = 1
				this.sort = {}
				this.isLoading = true
				this.search = ''
				const requestPayload = {
					formState: this.filters,
					pagination: 1,
				}
				this.forceRender()
				// eslint-disable-next-line no-undef
				const res = await axios.post(`${config.baseAPIurl}/validity-checker`, requestPayload)
				this.itemsVc = res.data.validityCheckerData
				this.itemsCount = res.data.allElementsCount
				this.applyServiceProviderRestriction = res.data.applyServiceProviderRestriction
				this.applyGenericUsageRestriction = res.data.applyGenericUsageKeyRestriction
				this.lastVcDataUpdateForMandator = moment(res.data.lastVcDataUpdate).format('YYYY-MM-DD HH:mm:ss')
				this.filterId = res.data.filterId
				this.$router.replace({
					query: { ...this.$route.query, filterId: this.filterId },
				})
			} catch (err) {
				// TODO: show alert
				if (err.response) {
					console.log('Error message:', err.response.data.message)
					console.log('Status code:', err.response.status)
				} else if (err.request) {
					console.log('No response received:', err.request)
				} else {
					console.log('Error:', err.message)
				}
			} finally {
				this.isLoading = false
				this.clickedSubmit = true
				this.doneFirstFetching = true
			}
		},
		async submitNewPage(value) {
			try {
				this.isLoading = true
				this.pagination = value
				const requestPayload = {
					formState: this.filters,
					pagination: value,
					sort: this.sort,
					search: this.search,
				}
				// eslint-disable-next-line no-undef
				const res = await axios.post(`${config.baseAPIurl}/validity-checker`, requestPayload)
				this.itemsVc = res.data.validityCheckerData
				this.itemsCount = res.data.allElementsCount
			} catch (err) {
				console.log(err)
			} finally {
				this.isLoading = false
				this.clickedSubmit = true
			}
		},
		async submitSort(value) {
			if (JSON.stringify(this.previousSort) === JSON.stringify(value)) {
				console.log('Sort unchanged, skipping API call')
				return
			}

			try {
				this.isLoading = true
				this.sort = value
				const requestPayload = {
					formState: this.filters,
					pagination: this.pagination,
					sort: value,
					search: this.search,
				}
				// eslint-disable-next-line no-undef
				const res = await axios.post(`${config.baseAPIurl}/validity-checker`, requestPayload)
				this.itemsVc = res.data.validityCheckerData
				this.itemsCount = res.data.allElementsCount
				this.previousSort = { ...value }
			} catch (err) {
				console.log(err)
			} finally {
				this.isLoading = false
				this.clickedSubmit = true
			}
		},
		async submitSearch() {
			try {
				this.isLoading = true
				const requestPayload = {
					formState: this.filters,
					pagination: 1,
					sort: this.sort,
					search: this.search,
				}
				// eslint-disable-next-line no-undef
				const res = await axios.post(`${config.baseAPIurl}/validity-checker`, requestPayload)
				this.itemsVc = res.data.validityCheckerData
				this.itemsCount = res.data.allElementsCount
			} catch (err) {
				console.log(err)
			} finally {
				this.isLoading = false
				this.clickedSubmit = true
			}
		},
		async excelExport() {
			try {
				this.isLoading = true
				this.alert.show = false
				await this.$nextTick()

				const { filterId } = this.$route.query
				const filterIdNumber = Number(filterId)

				await axios({
					method: 'post',
					url: `${config.baseAPIurl}/validity-checker/excel`,
					data: {
						filterId: filterIdNumber,
						expectedRows: this.itemsCount,
					},
				})
				// console.log('excelExport', res)
				this.alert.show = true
				this.alert.message = 'File creation triggered. See the file under your profile soon.'
				this.alert.type = 'info'
			} catch (err) {
				if (err.response && err.response.status === 400) {
					this.alert = {
						show: true,
						type: 'error',
						message: 'You have selected too many rows to export - max 100000 rows. Please reduce your selection or apply additional filters.',
					}
				} else {
					this.alert = {
						show: true,
						type: 'error',
						message: 'An error occurred while generating the Excel export.',
					}
				}
			} finally {
				this.isLoading = false
			}
		},
	},
	created() {
		const urlFilterId = this.$route.query.filterId
		if (urlFilterId) {
			this.filterId = urlFilterId
		}
	},
}

</script>

<style scoped>
.dividerVc{
	padding-top: 25px;
	padding-bottom: 25px;
	margin-top: 25px;
	margin-bottom: 25px;
}
</style>
